import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Button,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Textarea,
  Center,
  HStack,
  Image,
  VStack,
  Checkbox,
  Select,
  IconButton,
  Text,
  Box,
  Wrap,
  WrapItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  SimpleGrid,
} from "@chakra-ui/react";
import { FaPlus, FaTimes } from "react-icons/fa";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { useProductContext } from "../context/product_context";
import { categories_url } from "../utils/constants";
import { getSecureUrl } from "../utils/helpers";

function CreateNewProductModal() {
  const {
    new_product: {
      name,
      price,
      stock,
      description,
      colors,
      sizes,
      category,
      ShopName,
      shipping,
      featured,
    },
    updateNewProductDetails,
    createNewProduct,
  } = useProductContext();

  const [imageList, setImageList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryImage, setNewCategoryImage] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  const [selectedColors, setSelectedColors] = useState([]);
  const [customColor, setCustomColor] = useState("#000000");
  const [dimensions, setDimensions] = useState({
    longueur: "",
    largeur: "",
    hauteur: "",
  });

  // Predefined furniture colors
  const predefinedColors = [
    { name: "Noir", hex: "#000000" },
    { name: "Blanc", hex: "#FFFFFF" },
    { name: "Gris", hex: "#808080" },
    { name: "Beige", hex: "#F5F5DC" },
    { name: "Marron", hex: "#8B4513" },
    { name: "Chêne", hex: "#C4A484" },
    { name: "Noyer", hex: "#5C4033" },
    { name: "Wengé", hex: "#645452" },
    { name: "Or", hex: "#FFD700" },
    { name: "Argent", hex: "#C0C0C0" },
    { name: "Bronze", hex: "#CD7F32" },
    { name: "Bleu", hex: "#0066CC" },
    { name: "Vert", hex: "#228B22" },
    { name: "Rouge", hex: "#CC0000" },
  ];

  const addColor = (colorHex) => {
    if (!selectedColors.includes(colorHex)) {
      const newColors = [...selectedColors, colorHex];
      setSelectedColors(newColors);
      updateNewProductDetails({
        target: { name: "colors", value: newColors.join(",") },
      });
    }
  };

  const removeColor = (colorHex) => {
    const newColors = selectedColors.filter((c) => c !== colorHex);
    setSelectedColors(newColors);
    updateNewProductDetails({
      target: { name: "colors", value: newColors.join(",") },
    });
  };

  const handleDimensionChange = (field, value) => {
    const newDimensions = { ...dimensions, [field]: value };
    setDimensions(newDimensions);
    // Format as "L x l x H cm" for storage
    const formattedSize = `${newDimensions.longueur || 0} x ${newDimensions.largeur || 0} x ${newDimensions.hauteur || 0} cm`;
    updateNewProductDetails({
      target: { name: "sizes", value: formattedSize },
    });
  };

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(categories_url);
      const cats = (response.data.data || []).map((cat) => ({
        ...cat,
        image: cat.image ? { ...cat.image, url: getSecureUrl(cat.image.url) } : cat.image,
      }));
      setCategories(cats);
    } catch (error) {
      // Silent fail - categories will be empty
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setImageList((prev) => {
          return [...prev, reader.result];
        });
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const onCategoryImageDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewCategoryImage(reader.result);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/jpeg, image/png",
  });

  const {
    getRootProps: getCategoryRootProps,
    getInputProps: getCategoryInputProps,
    isDragActive: isCategoryDragActive,
  } = useDropzone({
    onDrop: onCategoryImageDrop,
    accept: "image/jpeg, image/png",
    multiple: false,
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialRef = useRef();
  const toast = useToast();

  const handleAddNewCategory = async () => {
    if (!newCategoryName || !newCategoryImage) {
      return toast({
        position: "top",
        description: "Please provide category name and image",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    setSavingCategory(true);
    try {
      await axios.post(
        `${categories_url}/admin`,
        { name: newCategoryName, image: newCategoryImage },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("acces_token"),
          },
        }
      );
      toast({
        position: "top",
        description: "Category created successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      await fetchCategories();
      // Set the new category as selected
      updateNewProductDetails({
        target: { name: "category", value: newCategoryName.toLowerCase() },
      });
      setShowNewCategoryForm(false);
      setNewCategoryName("");
      setNewCategoryImage("");
    } catch (error) {
      toast({
        position: "top",
        description: error.response?.data?.message || "Error creating category",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingCategory(false);
    }
  };

  const removeImage = (index) => {
    setImageList((prev) => {
      prev.splice(index, 1);
      return [...prev];
    });
  };

  const handleSubmit = async () => {
    if (
      !name ||
      !price ||
      !stock ||
      selectedColors.length < 1 ||
      (!dimensions.longueur || !dimensions.largeur || !dimensions.hauteur) ||
      !category ||
      !ShopName
    ) {
      return toast({
        position: "top",
        description: "Veuillez remplir tous les champs obligatoires (Nom, Prix, Stock, Catégorie, Matériau, Dimensions, Couleurs)",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    if (imageList.length < 1) {
      return toast({
        position: "top",
        description: "Add atleast one image",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    setLoading(true);
    const product = {
      name,
      price,
      stock,
      description: description || "",
      colors: selectedColors,
      sizes,
      category,
      ShopName,
      shipping,
      featured,
      images: imageList,
    };
    await createNewProduct(product);
    setLoading(false);

    onClose();
    return toast({
      position: "top",
      description: "Product created",
      status: "success",
      duration: 5000,
      isClosable: true,
    });

    // else {
    //   return toast({
    //     position: "top",
    //     description: responseCreate.message,
    //     status: "error",
    //     duration: 5000,
    //     isClosable: true,
    //   });
    // }
  };

  return (
    <>
      <Button colorScheme="brown" onClick={onOpen}>
        Create New Product
      </Button>

      <Modal initialFocusRef={initialRef} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create new product</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                ref={initialRef}
                placeholder="Product Name"
                name="name"
                focusBorderColor="brown.500"
                value={name}
                onChange={updateNewProductDetails}
              />
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Price</FormLabel>
              <Input
                type="number"
                placeholder="Product Price"
                name="price"
                focusBorderColor="brown.500"
                value={price}
                onChange={updateNewProductDetails}
              />
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Stock</FormLabel>
              <Input
                type="number"
                placeholder="Product Stock"
                name="stock"
                focusBorderColor="brown.500"
                value={stock}
                onChange={updateNewProductDetails}
              />
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Description (Optionnel)</FormLabel>
              <Textarea
                placeholder="Description du produit..."
                name="description"
                focusBorderColor="brown.500"
                value={description}
                onChange={updateNewProductDetails}
              />
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Category</FormLabel>
              <HStack>
                <Select
                  placeholder="Select Category"
                  name="category"
                  focusBorderColor="brown.500"
                  value={category}
                  onChange={updateNewProductDetails}
                  flex={1}
                >
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat.name}>
                      {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
                    </option>
                  ))}
                </Select>
                <IconButton
                  icon={<FaPlus />}
                  colorScheme="green"
                  onClick={() => setShowNewCategoryForm(!showNewCategoryForm)}
                  aria-label="Add new category"
                  title="Add new category"
                />
              </HStack>
              {showNewCategoryForm && (
                <VStack
                  mt={3}
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  spacing={3}
                  align="stretch"
                >
                  <Text fontWeight="bold" fontSize="sm">
                    Add New Category
                  </Text>
                  <Input
                    placeholder="Category Name"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    focusBorderColor="brown.500"
                    size="sm"
                  />
                  <Center
                    bg="white"
                    minHeight={80}
                    borderWidth={2}
                    borderColor="gray.200"
                    borderStyle="dashed"
                    borderRadius="md"
                    cursor="pointer"
                    {...getCategoryRootProps()}
                  >
                    {newCategoryImage ? (
                      <Image
                        src={newCategoryImage}
                        alt="Category"
                        maxH="70px"
                        objectFit="contain"
                      />
                    ) : isCategoryDragActive ? (
                      <Text fontSize="sm">Drop image here</Text>
                    ) : (
                      <Text fontSize="sm" textAlign="center" p={2}>
                        Drop category image or click
                      </Text>
                    )}
                  </Center>
                  <Input {...getCategoryInputProps()} />
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={handleAddNewCategory}
                    isLoading={savingCategory}
                    loadingText="Saving..."
                  >
                    Save Category
                  </Button>
                </VStack>
              )}
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Matériau</FormLabel>
              <Input
                placeholder="Ex: Métal et Panneaux MDF stratifié"
                name="ShopName"
                focusBorderColor="brown.500"
                value={ShopName}
                onChange={updateNewProductDetails}
              />
              <FormHelperText>Matériau principal du meuble</FormHelperText>
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Dimensions (en cm)</FormLabel>
              <HStack spacing={3}>
                <VStack align="start" flex={1}>
                  <Text fontSize="xs" color="gray.600">Longueur (L)</Text>
                  <Input
                    placeholder="80"
                    focusBorderColor="brown.500"
                    value={dimensions.longueur}
                    onChange={(e) => handleDimensionChange("longueur", e.target.value)}
                  />
                </VStack>
                <VStack align="start" flex={1}>
                  <Text fontSize="xs" color="gray.600">Largeur (l)</Text>
                  <Input
                    placeholder="45"
                    focusBorderColor="brown.500"
                    value={dimensions.largeur}
                    onChange={(e) => handleDimensionChange("largeur", e.target.value)}
                  />
                </VStack>
                <VStack align="start" flex={1}>
                  <Text fontSize="xs" color="gray.600">Hauteur (H)</Text>
                  <Input
                    placeholder="75"
                    focusBorderColor="brown.500"
                    value={dimensions.hauteur}
                    onChange={(e) => handleDimensionChange("hauteur", e.target.value)}
                  />
                </VStack>
              </HStack>
              <FormHelperText>Dimensions finales: {dimensions.longueur || '0'} x {dimensions.largeur || '0'} x {dimensions.hauteur || '0'} cm</FormHelperText>
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Couleurs</FormLabel>
              
              {/* Selected Colors Display */}
              {selectedColors.length > 0 && (
                <Wrap mb={3}>
                  {selectedColors.map((color, index) => (
                    <WrapItem key={index}>
                      <Box
                        display="flex"
                        alignItems="center"
                        bg="gray.100"
                        borderRadius="full"
                        px={2}
                        py={1}
                      >
                        <Box
                          w="20px"
                          h="20px"
                          borderRadius="full"
                          bg={color}
                          border="2px solid"
                          borderColor="gray.300"
                          mr={2}
                        />
                        <Text fontSize="sm" mr={2}>
                          {predefinedColors.find((c) => c.hex === color)?.name || color}
                        </Text>
                        <IconButton
                          icon={<FaTimes />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => removeColor(color)}
                          aria-label="Remove color"
                        />
                      </Box>
                    </WrapItem>
                  ))}
                </Wrap>
              )}

              {/* Predefined Colors Grid */}
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Couleurs prédéfinies:
              </Text>
              <SimpleGrid columns={7} spacing={2} mb={3}>
                {predefinedColors.map((color) => (
                  <Box
                    key={color.hex}
                    w="35px"
                    h="35px"
                    borderRadius="md"
                    bg={color.hex}
                    border="3px solid"
                    borderColor={selectedColors.includes(color.hex) ? "brown.500" : "gray.200"}
                    cursor="pointer"
                    onClick={() => addColor(color.hex)}
                    title={color.name}
                    _hover={{ transform: "scale(1.1)", borderColor: "brown.300" }}
                    transition="all 0.2s"
                  />
                ))}
              </SimpleGrid>

              {/* Custom Color Picker */}
              <HStack mt={3}>
                <Input
                  type="color"
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  w="60px"
                  h="40px"
                  p={1}
                  cursor="pointer"
                />
                <Input
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  placeholder="#000000"
                  w="100px"
                  size="sm"
                />
                <Button
                  size="sm"
                  colorScheme="brown"
                  onClick={() => addColor(customColor)}
                >
                  Ajouter
                </Button>
              </HStack>
              <FormHelperText>Sélectionnez les couleurs disponibles ou ajoutez une couleur personnalisée</FormHelperText>
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Images</FormLabel>
              <Center
                bg="brown.50"
                minHeight={100}
                my={5}
                borderWidth={3}
                borderColor="brown.200"
                borderStyle="dashed"
                borderRadius="lg"
                {...getRootProps()}
              >
                {isDragActive ? (
                  <p>Drag your files here</p>
                ) : (
                  <p>
                    Drag drop image files here, or click to select files
                    <br />
                    (Only *.jpeg and *.png images will be accepted)
                  </p>
                )}
              </Center>
              <Input {...getInputProps()} />
            </FormControl>

            <FormControl mt={4}>
              <HStack>
                {imageList.map((image, index) => {
                  return (
                    <VStack key={index} spacing={3}>
                      <Image
                        src={image}
                        boxSize="70px"
                        objectFit="cover"
                        borderRadius="lg"
                      />
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="red"
                        onClick={() => removeImage(index)}
                      >
                        Remove
                      </Button>
                    </VStack>
                  );
                })}
              </HStack>
            </FormControl>

            <FormControl mt={4}>
              <FormLabel color="green.500" fontWeight="bold">
                ✓ Free Shipping (All products have free shipping)
              </FormLabel>
            </FormControl>

            <FormControl mt={4}>
              <Checkbox
                name="featured"
                colorScheme="brown"
                isChecked={featured}
                onChange={updateNewProductDetails}
              >
                Featured
              </Checkbox>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              isLoading={loading}
              loadingText="Creating Product"
              colorScheme="brown"
              onClick={handleSubmit}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default CreateNewProductModal;
